import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Checkbox,
  TextField,
  InputAdornment,
  Typography,
  Paper,
  Divider,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { styled } from '@mui/material/styles';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    maxWidth: '600px',
    width: '100%',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: '24px 24px 16px',
  background: '#f8f9fa',
  color: '#353232ff',
  '& .MuiTypography-root': {
    fontSize: '20px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '24px',
  maxHeight: '500px',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '4px',
    '&:hover': {
      background: '#a8a8a8',
    },
  },
}));

const SearchContainer = styled(Box)(({ theme }) => ({
  marginBottom: '20px',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  backgroundColor: '#fff',
  paddingBottom: '12px',
}));

const ColumnsContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '8px',
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
  },
}));

const ColumnItem = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'selected' && prop !== 'itemIndex',
})(({ theme, selected, itemIndex }) => {
  return {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: `2px solid ${selected ? '#BD1D23' : 'transparent'}`,
    backgroundColor: selected ? 'rgb(248, 232, 232)' : '#fff',
    '&:hover': {
      backgroundColor: selected ? 'rgba(189, 29, 35, 0.1)' : 'rgba(189, 29, 35, 0.05)',
      transform: 'translateY(-2px)',
      boxShadow: '0 2px 8px rgba(189, 29, 35, 0.15)',
    },
    '& .MuiCheckbox-root': {
      padding: '8px',
      color: '#BD1D23',
      '&.Mui-checked': {
        color: '#BD1D23',
      },
    },
  };
});

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  padding: '12px 16px',
  backgroundColor: '#f8f9fa',
  borderRadius: '8px',
  marginBottom: '16px',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 500,
  padding: '8px 16px',
}));

const SelectedCount = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#3d3b3bff',
  fontWeight: 600,
  marginLeft: 'auto',
  padding: '4px 12px',
  borderRadius: '12px',
}));

const ColumnSelectionModal = ({ open, onClose, columns, onDownload, data }) => {
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize with no columns selected (empty set)
  useEffect(() => {
    if (open) {
      setSelectedColumns(new Set());
    }
  }, [open]);

  const handleToggleColumn = (accessorKey) => {
    const newSelected = new Set(selectedColumns);
    if (newSelected.has(accessorKey)) {
      newSelected.delete(accessorKey);
    } else {
      newSelected.add(accessorKey);
    }
    setSelectedColumns(newSelected);
  };

  const handleSelectAll = () => {
    const allColumnKeys = new Set(columns.map((col) => col.accessorKey));
    setSelectedColumns(allColumnKeys);
  };

  const handleDeselectAll = () => {
    setSelectedColumns(new Set());
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value.toLowerCase());
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const filteredColumns = columns.filter(
    (col) =>
      col.header.toLowerCase().includes(searchQuery) ||
      col.accessorKey.toLowerCase().includes(searchQuery)
  );

  const handleDownloadClick = () => {
    if (selectedColumns.size === 0) {
      return;
    }
    onDownload(Array.from(selectedColumns));
    onClose();
    setSearchQuery('');
  };

  const handleCancel = () => {
    onClose();
    setSearchQuery('');
  };

  return (
    <StyledDialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <StyledDialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography variant="h6" component="div">
            Select Columns for Download
          </Typography>
          <SelectedCount>
            {selectedColumns.size} of {columns.length} selected
          </SelectedCount>
        </Box>
      </StyledDialogTitle>

      <StyledDialogContent>
        <SearchContainer>
          <TextField
            fullWidth
            placeholder="Search columns..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#999' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                '&:hover': {
                  backgroundColor: '#f0f0f0',
                },
                '&.Mui-focused': {
                  backgroundColor: '#fff',
                },
              },
              marginTop: '8px',
            }}
          />
        </SearchContainer>

        <ActionButtons>
          <StyledButton
            variant="outlined"
            startIcon={<SelectAllIcon />}
            onClick={handleSelectAll}
            size="small"
            sx={{ borderColor: '#1d7dbdff', color: '#1d82bdff' }}
          >
            Select All
          </StyledButton>
          <StyledButton
            variant="outlined"
            startIcon={<CheckBoxOutlineBlankIcon />}
            onClick={handleDeselectAll}
            size="small"
            sx={{ borderColor: '#BD1D23', color: '#BD1D23' }}
          >
            Deselect All
          </StyledButton>
        </ActionButtons>

        <Divider sx={{ marginBottom: '16px' }} />

        <ColumnsContainer>
          {filteredColumns.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                padding: '40px 20px',
                color: '#999',
              }}
            >
              <Typography variant="body2">No columns found matching your search.</Typography>
            </Box>
          ) : (
            filteredColumns.map((column, index) => {
              const isSelected = selectedColumns.has(column.accessorKey);
              return (
                <ColumnItem
                  key={column.accessorKey}
                  selected={isSelected}
                  itemIndex={index}
                  onClick={() => handleToggleColumn(column.accessorKey)}
                  elevation={isSelected ? 2 : 0}
                >
                  <Checkbox
                    checked={isSelected}
                    onChange={() => handleToggleColumn(column.accessorKey)}
                    sx={{
                      '&.Mui-checked': {
                        color: '#BD1D23',
                      },
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      flex: 1,
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? '#BD1D23' : '#333',
                    }}
                  >
                    {column.header}
                  </Typography>
                </ColumnItem>
              );
            })
          )}
        </ColumnsContainer>
      </StyledDialogContent>

      <DialogActions sx={{ padding: '16px 24px', backgroundColor: '#f8f9fa' }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            color: '#666',
            borderColor: '#ddd',
            '&:hover': {
              borderColor: '#999',
              backgroundColor: '#f0f0f0',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDownloadClick}
          variant="contained"
          disabled={selectedColumns.size === 0}
          sx={{
            borderRadius: '8px',
            textTransform: 'none',
            background: '#BD1D23',
            boxShadow: '0 4px 12px rgba(189, 29, 35, 0.4)',
            '&:hover': {
              background: '#a0181e',
              boxShadow: '0 6px 16px rgba(189, 29, 35, 0.5)',
            },
            '&:disabled': {
              background: '#ccc',
              boxShadow: 'none',
            },
          }}
        >
          Download ({selectedColumns.size} columns)
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ColumnSelectionModal;
